/**
 * Start the engine from inside the app.
 *
 * Nova is two processes, which is Nova's business and not the curator's. When
 * the shell cannot start the engine on launch -- the app installed away from the
 * engine's files, a crash on startup, an engine stopped by hand -- the old
 * behaviour was a window saying "engine offline · sample data" with nothing to
 * press. This is the something to press: it starts the engine, waits for it to
 * answer, and if it will not, shows why along with the engine's own last words.
 *
 * "Choose where Nova's engine is" covers the installed case: point it at the
 * project folder (the one holding services/analyzer), or at a folder holding a
 * frozen nova-engine. The shell remembers the choice, so the next launch starts
 * by itself.
 *
 * In a browser tab there is no shell to start anything, so it says so instead.
 */

import { useState } from "react";
import { isDesktop, pickFolder, restartEngine, startEngine } from "./platform";

type Phase = "idle" | "starting" | "failed";

export function EngineControl({
  state,
  onStarted,
}: {
  /** The library's connection state, so this only appears when it is needed. */
  state: "connecting" | "connected" | "offline";
  onStarted: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const [showLog, setShowLog] = useState(false);

  const run = async (action: () => Promise<{ log?: string[] }>) => {
    setPhase("starting");
    setError(null);
    setLog([]);
    try {
      const info = await action();
      setPhase("idle");
      setLog(info.log ?? []);
      onStarted();
    } catch (err) {
      setPhase("failed");
      setError(err instanceof Error ? err.message : String(err));
      // The reason usually lives in the engine's own output, not in the error.
      try {
        const { engineInfo } = await import("./platform");
        setLog((await engineInfo()).log ?? []);
      } catch {
        // Nothing more to show than the message itself.
      }
    }
  };

  if (state === "connected") {
    return isDesktop() ? (
      <button
        className="ghost engine-restart"
        disabled={phase === "starting"}
        title="Stop the engine this app started and start it again — after changing engine code"
        onClick={() => void run(restartEngine)}
      >
        {phase === "starting" ? "Restarting…" : "Restart engine"}
      </button>
    ) : null;
  }

  if (!isDesktop()) {
    return (
      <div className="engine-help">
        This tab cannot start the engine. Run <code>Start Nova.bat</code>, or start it with{" "}
        <code>python -m nova.api</code>.
      </div>
    );
  }

  return (
    <div className="engine-start">
      <button className="scan-button" disabled={phase === "starting"} onClick={() => void run(() => startEngine())}>
        {phase === "starting" ? "Starting the engine…" : "Start engine"}
      </button>
      {phase === "failed" && (
        <>
          <div className="engine-help bad">{error}</div>
          <button
            className="ghost"
            onClick={async () => {
              const folder = await pickFolder();
              if (folder) await run(() => startEngine(folder));
            }}
          >
            Choose where Nova's engine is…
          </button>
          {log.length > 0 && (
            <>
              <button className="ghost" onClick={() => setShowLog((s) => !s)}>
                {showLog ? "Hide" : "Show"} what the engine said
              </button>
              {showLog && <pre className="engine-log">{log.join("\n")}</pre>}
            </>
          )}
        </>
      )}
    </div>
  );
}
